import type { Route } from './+types/about';
import { ContentSection } from '@/components/sections/content-section';
import { CtaBanner } from '@/components/sections/cta-banner';
import { HeroSection } from '@/components/sections/hero-section';
import { ValueProposition } from '@/components/sections/value-proposition';
import { inheritRootLdJson } from '@/utils/schema';

export function meta({ matches }: Route.MetaArgs) {
  return [
    ...inheritRootLdJson(matches),
    { title: 'About Us | Meet Real Estate — Kingaroy & South Burnett' },
    {
      name: 'description',
      content:
        'Meet Lucio Passos — 26 years of real estate experience across Latin America and Australia, now serving Kingaroy and the South Burnett with honest, personal service, start to finish.',
    },
  ];
}

export default function About() {
  return (
    <main>
      <HeroSection
        eyebrow="About Us"
        heading="From South America to South Burnett."
        description="We turn up, when others don't."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
      />

      <ContentSection
        id="story"
        className="scroll-mt-20"
        eyebrow="Our Story"
        heading="Twenty-six years, three markets, one principle."
        image="/static/home/lucio-portrait.png"
        imageAlt="Lucio Passos"
        paragraphs={[
          'Lucio Passos began his real estate career at just 17 years old, helping in his father’s agency. His first role involved managing the financing process for more than 40 clients at the same time on a large development.',
          'From there he spent six years as a sales agent at Lopes Consultoria de Imóveis, the largest real estate company in Latin America. He then moved into a sales management role at Brasil Brokers, another major player in the Latin American market.',
          'After that, Lucio ran his own real estate agency for seven years before taking on a short-term Sales Director position with a construction company in São Paulo.',
          'In 2022 he came to Australia to visit his brothers and nieces, stayed to improve his English, and met the love of his life — Maria Fernanda Passos. Maria Fernanda is a qualified accountant with more than 17 years’ experience and has worked in administration for over 20 years (graduating in 2010).',
          'As people of faith, Lucio and Maria Fernanda believe their paths crossed for a reason. Meet Real Estate was created from the combination of Maria Fernanda’s steady, professional administration and Lucio’s extensive experience in sales, people management and client relationships.',
          'Together they bring a calm, reliable and personal approach to property in Kingaroy and the South Burnett — always guided by the simple principle: we turn up, when others don’t.',
        ]}
      />

      <ValueProposition
        id="why-us"
        className="section--navy-mid scroll-mt-20"
        eyebrow="Why Meet Real Estate"
        heading="What makes us different"
        align="center"
        items={[
          {
            title: 'One agent, start to finish',
            description:
              'No handoffs, no junior staff. You deal directly with Lucio from the first conversation through to settlement.',
          },
          {
            title: 'Local knowledge, global experience',
            description:
              '26 years across three international property markets, brought home to Kingaroy and the South Burnett.',
          },
          {
            title: 'Honest advice, not a sales pitch',
            description: "A realistic appraisal and straight answers — even when they're not what you want to hear.",
          },
          {
            title: "We turn up, when others don't",
            description: 'Reachable seven days a week, long after the contract is signed.',
          },
        ]}
      />

      <CtaBanner
        id="contact"
        className="scroll-mt-20"
        eyebrow="Contact"
        heading="Ready to talk?"
        description="Buying, selling, renting, or just curious about the market — a conversation costs you nothing."
        primaryCta={{ label: 'Request an appraisal', to: '/appraisal' }}
        secondaryCta={{ label: 'Call Lucio', to: 'tel:0493035628' }}
        contactName="Lucio Passos"
        contactTitle="Sales Agent, Meet Real Estate"
        contactLinks={[
          { label: 'Mobile', value: '0493 035 628', href: 'tel:0493035628' },
          { label: 'Office', value: '07 3355 7777', href: 'tel:0733557777' },
          { label: 'Email', value: 'lucio@meetrealestate.com.au', href: 'mailto:lucio@meetrealestate.com.au' },
          { label: 'Website', value: 'meetrealestate.com.au', href: 'https://meetrealestate.com.au' },
        ]}
      />
    </main>
  );
}
